import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { encodeFiltersToUrl, decodeFiltersFromUrl, areFilterModelsEqual } from '../utils/filterUrlUtils';

/**
 * Custom hook for syncing DataGrid filter state with URL parameters
 * Provides shareable URLs for filtered views with compression
 * 
 * @param {string} paramName - URL parameter name (default: 'f')
 * @returns {Array} [filterModel, setFilterModel] - State and setter for filter model
 */
export function useFilterUrl(paramName = 'f') {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize filter model from URL
  const [filterModel, setFilterModelState] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlParam = searchParams.get(paramName);
    return decodeFiltersFromUrl(urlParam);
  });

  // Update URL when filter model changes
  const setFilterModel = useCallback((newFilterModel) => {
    setFilterModelState(newFilterModel);
    
    // Encode filters to URL parameter
    const urlParam = encodeFiltersToUrl(newFilterModel);
    const searchParams = new URLSearchParams(location.search);
    
    if (urlParam) {
      searchParams.set(paramName, urlParam);
    } else {
      searchParams.delete(paramName);
    }
    
    // Use replace to avoid cluttering browser history
    const newSearch = searchParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
    // Only navigate if URL actually changed
    if (newUrl !== `${location.pathname}${location.search}`) {
      navigate(newUrl, { replace: true });
    }
  }, [location.pathname, location.search, navigate, paramName]);

  // Update filter model when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlParam = searchParams.get(paramName);
    const urlFilterModel = decodeFiltersFromUrl(urlParam);
    
    // Only update if the URL filter model is different from current state
    if (!areFilterModelsEqual(filterModel, urlFilterModel)) {
      setFilterModelState(urlFilterModel);
    }
  }, [location.search, paramName]); // Intentionally excluding filterModel to avoid loops

  return [filterModel, setFilterModel];
}